import { useEffect, useState } from "react";
import { t } from "../../shared/i18n";
import { DICE_LIMITS, rollDice, type DiceRoll, type DiceType } from "./dice";
import { loadDiceHistory, saveDiceRoll } from "./diceStore";

type DiceCopy = {
  kicker: string;
  title: string;
  description: string;
  localLimits: string;
  limitDescription: string;
  ready: string;
  recent: string;
};

function copyFor(locale: string): DiceCopy {
  return locale === "en"
    ? {
        kicker: "Roll panel",
        title: "Throw the dice",
        description:
          "Local rolls use secure browser randomness and stay on this device.",
        localLimits: "Local limits",
        limitDescription:
          "Fixed client-side limits; no server settings are used.",
        ready: "Ready when you are.",
        recent: "Recent rolls",
      }
    : {
        kicker: "Панель броска",
        title: "Бросить кубики",
        description:
          "Локальные броски используют криптографическую случайность браузера и остаются на этом устройстве.",
        localLimits: "Локальные лимиты",
        limitDescription:
          "Фиксированные лимиты на клиенте; серверные настройки не используются.",
        ready: "Готов к броску.",
        recent: "Последние броски",
      };
}

export function DicePanel({ locale }: { locale: string }) {
  const [diceType, setDiceType] = useState<DiceType>("d20");
  const [count, setCount] = useState(1);
  const [lastRoll, setLastRoll] = useState<DiceRoll | null>(null);
  const [history, setHistory] = useState<DiceRoll[]>([]);
  const copy = copyFor(locale);

  useEffect(() => {
    loadDiceHistory()
      .then(setHistory)
      .catch(() => setHistory([]));
  }, []);

  async function roll() {
    const result = rollDice(diceType, count);
    setLastRoll(result);
    setHistory(await saveDiceRoll(result));
  }

  return (
    <div className="dice-page">
      <section className="hero pipboy-frame">
        <div>
          <p className="eyebrow">OFFLINE DICE</p>
          <h1>{copy.title}</h1>
          <p className="hero-copy">{copy.description}</p>
        </div>
        <div className="hero-badge">CRYPTO RANDOM • LOCAL HISTORY</div>
      </section>

      <section className="dashboard">
        <section className="card roll-card">
          <header className="card-head">
            <div>
              <p className="card-kicker">{copy.kicker}</p>
              <h2>{copy.title}</h2>
            </div>
            <div className="limits-inline" aria-label={copy.localLimits}>
              <span>d20 × {DICE_LIMITS.d20}</span>
              <span>d6 × {DICE_LIMITS.d6}</span>
            </div>
          </header>
          <div className="dice-switch" role="tablist" aria-label="Dice type">
            {(["d20", "d6"] as DiceType[]).map((type) => (
              <button
                className={`chip${diceType === type ? " is-active" : ""}`}
                type="button"
                key={type}
                role="tab"
                aria-selected={diceType === type}
                onClick={() => {
                  setDiceType(type);
                  setCount(1);
                }}
              >
                {type}
              </button>
            ))}
          </div>
          <div className="field-row">
            <label className="field">
              <span>{t(locale, "diceCount")}</span>
              <input
                aria-label={t(locale, "diceCount")}
                type="number"
                min="1"
                max={DICE_LIMITS[diceType]}
                value={count}
                onChange={(event) =>
                  setCount(
                    Math.max(
                      1,
                      Math.min(
                        DICE_LIMITS[diceType],
                        Number(event.target.value) || 1,
                      ),
                    ),
                  )
                }
              />
              <small>MAX {DICE_LIMITS[diceType]}</small>
            </label>
            <button className="cta" type="button" onClick={() => void roll()}>
              {t(locale, "roll")}
            </button>
          </div>
          <div className="result-card" aria-live="polite">
            <div className="result-total">{lastRoll?.total ?? 0}</div>
            <div className="result-meta">
              {lastRoll
                ? `${lastRoll.diceType.toUpperCase()} × ${lastRoll.count}`
                : copy.ready}
            </div>
            <div className="roll-values">{lastRoll?.rolls.join(" • ")}</div>
          </div>
        </section>

        <aside className="side-stack">
          <section className="card">
            <header className="card-head">
              <div>
                <p className="card-kicker">CONFIGURATION</p>
                <h2>{copy.localLimits}</h2>
              </div>
            </header>
            <div className="limits-inline">
              <strong>d20: {DICE_LIMITS.d20}</strong>
              <strong>d6: {DICE_LIMITS.d6}</strong>
            </div>
            <p className="microcopy">{copy.limitDescription}</p>
          </section>
          <section className="card">
            <header className="card-head">
              <div>
                <p className="card-kicker">{t(locale, "history")}</p>
                <h2>{copy.recent}</h2>
              </div>
            </header>
            <div className="history-list">
              {history.length ? (
                history.map((roll) => (
                  <article className="history-item" key={roll.id}>
                    <div>
                      <strong>
                        {roll.diceType.toUpperCase()} × {roll.count}
                      </strong>
                      <p>{new Date(roll.createdAt).toLocaleString(locale)}</p>
                    </div>
                    <div className="history-rolls">{roll.rolls.join(", ")}</div>
                    <div className="history-total">{roll.total}</div>
                  </article>
                ))
              ) : (
                <p className="empty-state">{copy.ready}</p>
              )}
            </div>
          </section>
        </aside>
      </section>
    </div>
  );
}
